import { useEffect, useState } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Landmark, Save, ShieldCheck, Users, Calculator, User as UserIcon, Info, FileDown, Lock, Pencil } from 'lucide-react';

function BankingFields({ value, onChange }) {
  return (
    <>
      <div>
        <Label>Account Holder Name</Label>
        <Input value={value.account_holder} onChange={(e) => onChange({ ...value, account_holder: e.target.value })} placeholder="Full legal name" />
      </div>
      <div>
        <Label>Bank Name</Label>
        <Input value={value.bank_name} onChange={(e) => onChange({ ...value, bank_name: e.target.value })} placeholder="e.g., Chase, Bank of America" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Routing (9 digits)</Label>
          <Input value={value.routing_number} onChange={(e) => onChange({ ...value, routing_number: e.target.value.replace(/\D/g, '').slice(0, 9) })} placeholder="123456789" inputMode="numeric" />
        </div>
        <div>
          <Label>Account (4-17 digits)</Label>
          <Input value={value.account_number} onChange={(e) => onChange({ ...value, account_number: e.target.value.replace(/\D/g, '').slice(0, 17) })} placeholder="12345678" inputMode="numeric" />
        </div>
      </div>
      <div>
        <Label>Account Type</Label>
        <Select value={value.account_type} onValueChange={(v) => onChange({ ...value, account_type: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="checking">Checking</SelectItem>
            <SelectItem value="savings">Savings</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </>
  );
}

export default function Profile({ user }) {
  const [banking, setBanking] = useState({ account_holder: '', bank_name: '', routing_number: '', account_number: '', account_type: 'checking' });
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [teamStatus, setTeamStatus] = useState(null);
  const [gross, setGross] = useState(3500);
  const [split, setSplit] = useState(null);
  const [commissionSettings, setCommissionSettings] = useState({ dispatch_fee_percent: 12, factoring_percent: 4 });
  const [editingMember, setEditingMember] = useState(null);
  const [memberBanking, setMemberBanking] = useState({ account_holder: '', bank_name: '', routing_number: '', account_number: '', account_type: 'checking' });
  const [memberSaving, setMemberSaving] = useState(false);

  const isMgr = user?.role === 'super_admin' || user?.role === 'broker' || user?.role === 'broker_manager';

  useEffect(() => {
    api.get('/users/me/banking').then(r => setSaved(r.data)).catch(() => {});
    api.get('/org/commission-settings').then(r => setCommissionSettings(r.data)).catch(() => {});
    if (isMgr) api.get('/admin/team-banking-status').then(r => setTeamStatus(r.data.members || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (gross > 0) {
      api.post('/settlements/calculate-preview', {
        gross: Number(gross),
        dispatch_fee_pct: Number(commissionSettings.dispatch_fee_percent),
        factoring_pct: Number(commissionSettings.factoring_percent),
      }).then(r => setSplit(r.data)).catch(() => setSplit(null));
    }
  }, [gross, commissionSettings]);

  const saveBanking = async () => {
    setSaving(true);
    try {
      await api.put('/users/me/banking', banking);
      toast.success('Banking info saved securely');
      setBanking({ ...banking, routing_number: '', account_number: '' });
      api.get('/users/me/banking').then(r => setSaved(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const saveMemberBanking = async () => {
    if (!editingMember) return;
    setMemberSaving(true);
    try {
      await api.put(`/admin/users/${editingMember.id}/banking`, memberBanking);
      toast.success(`Banking saved for ${editingMember.name}`);
      setEditingMember(null);
      api.get('/admin/team-banking-status').then(r => setTeamStatus(r.data.members || []));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setMemberSaving(false); }
  };

  const saveCommissionSettings = async () => {
    try {
      await api.put('/org/commission-settings', {
        dispatch_fee_percent: Number(commissionSettings.dispatch_fee_percent),
        factoring_percent: Number(commissionSettings.factoring_percent),
      });
      toast.success('Split rules saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const downloadPayout = async (uid = null) => {
    try {
      const res = await api.get(uid ? `/reports/weekly-payout?user_id=${uid}` : '/reports/weekly-payout', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const dl = document.createElement('a');
      dl.href = URL.createObjectURL(blob);
      dl.download = `payout_${new Date().toISOString().slice(0, 10)}.pdf`;
      dl.click();
      URL.revokeObjectURL(dl.href);
    } catch { toast.error('Failed to generate payout PDF'); }
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
          <UserIcon className="w-8 h-8 text-blue-600" /> My Profile & Payouts
        </h1>
        <p className="text-slate-600 mt-1">{user?.name} · {user?.role?.replace('_', ' ')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Own banking */}
        <Card data-testid="banking-card">
          <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Landmark className="w-5 h-5 text-blue-600" />Direct Deposit Banking</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {saved?.has_banking_info && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs flex-1">
                  <p className="font-semibold text-green-800 flex items-center gap-1">
                    Banking on file
                    {saved.encrypted && (<span className="inline-flex items-center gap-1 bg-green-200 text-green-900 px-1.5 rounded font-bold text-[9px] uppercase"><Lock className="w-2.5 h-2.5" /> Encrypted</span>)}
                  </p>
                  <p className="text-slate-700 mt-0.5">{saved.bank_name} · {saved.account_type} · {saved.account_number_masked}</p>
                  {saved.updated_at && <p className="text-slate-500 mt-0.5">Last updated {new Date(saved.updated_at).toLocaleDateString()}</p>}
                  <Button size="sm" variant="outline" className="mt-2 h-7 text-[11px]" onClick={() => downloadPayout()} data-testid="download-weekly-payout-btn">
                    <FileDown className="w-3 h-3 mr-1" /> Download this week's payout PDF
                  </Button>
                </div>
              </div>
            )}
            <BankingFields value={banking} onChange={setBanking} />
            <Button onClick={saveBanking} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="banking-save-btn">
              <Save className="w-4 h-4 mr-2" />{saved?.has_banking_info ? 'Update Banking Info' : 'Save Banking Info'}
            </Button>
            <p className="text-[10px] text-slate-500">🔒 Routing & account numbers are encrypted at rest. Only the last 4 digits are ever shown.</p>
          </CardContent>
        </Card>

        {/* Split preview */}
        <Card data-testid="split-preview-card">
          <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Calculator className="w-5 h-5 text-amber-600" />Payout Split Preview</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-700 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-500" />
              <span>Factoring takes <strong>{commissionSettings.factoring_percent}%</strong>, then a <strong>{commissionSettings.dispatch_fee_percent}%</strong> dispatch fee is split 50/50 between <strong>Agent</strong> and <strong>Broker/Admin</strong>.</span>
            </div>
            {isMgr && (
              <div className="grid grid-cols-2 gap-2 border border-slate-200 rounded p-2 bg-slate-50">
                <div><Label className="text-[10px]">Factoring %</Label><Input type="number" step="0.5" value={commissionSettings.factoring_percent} onChange={(e) => setCommissionSettings({ ...commissionSettings, factoring_percent: Number(e.target.value) })} className="h-8 text-sm" /></div>
                <div><Label className="text-[10px]">Dispatch fee %</Label><Input type="number" step="0.5" value={commissionSettings.dispatch_fee_percent} onChange={(e) => setCommissionSettings({ ...commissionSettings, dispatch_fee_percent: Number(e.target.value) })} className="h-8 text-sm" /></div>
                <Button size="sm" variant="outline" className="col-span-2 h-8 text-xs" onClick={saveCommissionSettings} data-testid="save-commission-btn">Save split rules</Button>
              </div>
            )}
            <div><Label>Load Gross Revenue ($)</Label><Input type="number" min={0} value={gross} onChange={(e) => setGross(e.target.value)} data-testid="split-gross-input" /></div>
            {split && (
              <div className="space-y-2" data-testid="split-preview-result">
                <SplitRow label="Gross" value={split.gross} bold />
                <SplitRow label={`Factoring cut (${split.factoring_pct}%)`} value={split.factoring_amount} color="red" />
                <SplitRow label={`Dispatch fee pool (${split.dispatch_fee_pct}%)`} value={split.total_fee} />
                <div className="pl-6 space-y-2">
                  <SplitRow label="Agent share (50%)" value={split.agent_share} color="green" />
                  <SplitRow label="Broker / Admin share (50%)" value={split.company_share} color="purple" />
                </div>
                <SplitRow label="Net to carrier" value={split.net_to_carrier} color="blue" bold />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Team roster */}
      {isMgr && teamStatus && (
        <Card data-testid="team-banking-card">
          <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Users className="w-5 h-5 text-blue-600" />Team Banking ({teamStatus.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
                    <th className="py-2 px-3">Name</th><th className="py-2 px-3">Role</th><th className="py-2 px-3">Banking</th><th className="py-2 px-3">Bank</th><th className="py-2 px-3">Account</th><th className="py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teamStatus.length === 0 ? (
                    <tr><td colSpan={6} className="py-6 text-center text-slate-500">No team members</td></tr>
                  ) : teamStatus.map(m => (
                    <tr key={m.id} className="border-b hover:bg-slate-50" data-testid={`team-row-${m.id}`}>
                      <td className="py-2 px-3 font-medium text-slate-900">{m.name}</td>
                      <td className="py-2 px-3 text-xs text-slate-600 capitalize">{m.role?.replace('_', ' ')}</td>
                      <td className="py-2 px-3">
                        {m.has_banking_info ? (
                          <span className="text-[11px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">ON FILE</span>
                        ) : (
                          <span className="text-[11px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded">MISSING</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-slate-700">{m.bank_name || '—'}</td>
                      <td className="py-2 px-3 text-slate-700 font-mono text-xs">{m.account_number_masked || '—'}</td>
                      <td className="py-2 px-3 flex gap-1">
                        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => { setEditingMember(m); setMemberBanking({ account_holder: '', bank_name: '', routing_number: '', account_number: '', account_type: 'checking' }); }} data-testid={`edit-banking-${m.id}`}>
                          <Pencil className="w-3 h-3 mr-1" />{m.has_banking_info ? 'Update' : 'Add'}
                        </Button>
                        {m.has_banking_info && (
                          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => downloadPayout(m.id)} data-testid={`payout-pdf-${m.id}`}>
                            <FileDown className="w-3 h-3 mr-1" />PDF
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin banking edit dialog */}
      <Dialog open={!!editingMember} onOpenChange={(o) => !o && setEditingMember(null)}>
        <DialogContent data-testid="admin-banking-dialog">
          <DialogHeader><DialogTitle>Add / Update Banking for {editingMember?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <BankingFields value={memberBanking} onChange={setMemberBanking} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingMember(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={saveMemberBanking} disabled={memberSaving} data-testid="admin-banking-save-btn">
              <Save className="w-4 h-4 mr-2" />Save Banking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SplitRow({ label, value, color, bold }) {
  const palette = { red: 'text-red-600', green: 'text-green-700', purple: 'text-purple-700', blue: 'text-blue-700' };
  return (
    <div className={`flex items-center justify-between text-sm ${bold ? 'font-bold' : ''} border-b border-slate-100 pb-1`}>
      <span className="text-slate-700">{label}</span>
      <span className={`${palette[color] || 'text-slate-800'} font-mono`}>${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
    </div>
  );
}
